import axios from 'axios';

const BASE_URL = process.env.REACT_APP_BASE_URL;

const getHeaders = (token) => ({
  'Authorization': `Bearer ${token}`,
  'Content-Type': 'application/json'
});

const handleError = (error) => {
  console.error('Department API Error:', error);
  if (error.response?.status === 503) {
    return { success: false, error: 'Service temporarily unavailable' };
  }
  return {
    success: false,
    error: error.response?.data?.message || error.message || 'Operation failed'
  };
};

export const createDepartment = async (departmentData, token) => {
  try {
    const response = await axios.post(
      `${BASE_URL}/user/v1/department`,
      { name: departmentData.name, client_id: departmentData.clientId },
      { headers: getHeaders(token) }
    );

    if (!response.data.data) throw new Error('No department data received');

    return {
      success: response.data.status === 0,
      department: response.data.data
    };
  } catch (error) {
    return handleError(error);
  }
};

export const activateDepartment = async (departmentId, token) => {
  try {
    const response = await axios.post(
      `${BASE_URL}/user/v1/department/activate`,
      { department_id: departmentId },
      { headers: getHeaders(token) }
    );

    return {
      success: response.data.status === 0,
      message: response.data.message
    };
  } catch (error) {
    return handleError(error);
  }
};

export const getDepartmentById = async (departmentId, token) => {
  try {
    const response = await axios.get(
      `${BASE_URL}/user/v1/department/${departmentId}`,
      { headers: getHeaders(token) }
    );

    if (!response.data.data) throw new Error('No department data received');

    return {
      success: response.data.status === 0,
      department: response.data.data
    };
  } catch (error) {
    return handleError(error);
  }
};

export const getAllDepartments = async (params, token) => {
  try {
    const queryParams = new URLSearchParams();

    if (params.pagination) {
      queryParams.append('pagination.page', params.pagination.page || 1);
      queryParams.append('pagination.page_size', params.pagination.pageSize || 10);
    }

    if (params.sorting) {
      queryParams.append('sorting.sort_by', params.sorting.sort_by);
      queryParams.append('sorting.order', params.sorting.order);
    }

    if (params.nameFilter) queryParams.append('name_filter', params.nameFilter);
    if (params.clientId) queryParams.append('client_id', params.clientId);

    const response = await axios.get(
      `${BASE_URL}/user/v1/departments?${queryParams.toString()}`,
      { headers: getHeaders(token) }
    );

    const data = response.data;
    if (!data.data) throw new Error('No departments data received');

    return {
      success: data.status === 0,
      departments: data.data,
      pagination: {
        totalCount: data.totalCount,
        pageCount: data.pageCount,
        page: data.page,
        pageSize: data.pageSize
      }
    };
  } catch (error) {
    return handleError(error);
  }
};

export const updateDepartment = async (departmentData, token) => {
  try {
    const response = await axios.patch(
      `${BASE_URL}/user/v1/department/${departmentData.id}`,
      { id: departmentData.id, name: departmentData.name },
      { headers: getHeaders(token) }
    );

    if (!response.data.data) throw new Error('No department data received');

    return {
      success: response.data.status === 0,
      department: response.data.data
    };
  } catch (error) {
    return handleError(error);
  }
};

export const deleteDepartment = async (departmentId, token) => {
  try {
    const response = await axios.delete(
      `${BASE_URL}/user/v1/department/${departmentId}`,
      { headers: getHeaders(token) }
    );

    return {
      success: response.data.status === 0,
      message: response.data.message
    };
  } catch (error) {
    return handleError(error);
  }
};

export const removeUserFromDepartment = async (departmentId, userId, token) => {
  try {
    const response = await axios.delete(
      `${BASE_URL}/user/v1/department/${departmentId}/user/${userId}`,
      { headers: getHeaders(token) }
    );

    return {
      success: response.data.status === 0,
      message: response.data.message
    };
  } catch (error) {
    return handleError(error);
  }
};

export const getAllDepartmentAdminsCredentials = async (departmentId, token) => {
  try {
    const response = await axios.get(
      `${BASE_URL}/user/v1/department/${departmentId}/admins`,
      { headers: getHeaders(token) }
    );

    return {
      success: response.data.status === 0,
      admins: response.data.data,
      message: response.data.message
    };
  } catch (error) {
    return handleError(error);
  }
};
